<template>
  <div class="min-h-screen bg-[#f8f7f4]">
    <section class="mx-auto max-w-3xl px-6 py-14">
      <div class="mb-10 text-center">
        <p class="text-xs font-semibold uppercase tracking-[0.25em] text-brand">Become a Vendor</p>
        <h1 class="mt-3 text-3xl font-semibold text-ink">Vendor Signup</h1>
        <p class="mt-2 text-sm text-muted">Create your vendor account with the details below.</p>
      </div>

      <form class="rounded-[28px] bg-white px-6 py-8 shadow-card ring-1 ring-black/5 sm:px-10" @submit.prevent="submit">
        <div class="space-y-6">
          <label class="field-block">
            <div class="field-shell" :class="fieldShellClass('brandName', focusedField === 'brandName', form.brandName)">
              <span class="field-floating-label">Vendor name*</span>
              <input
                v-model.trim="form.brandName"
                type="text"
                class="field-control"
                @focus="focusedField = 'brandName'"
                @blur="focusedField = ''"
              >
            </div>
            <span v-if="errors.brandName" class="error-text">{{ errors.brandName }}</span>
          </label>
 

          <label class="field-block">
            <div class="field-shell" :class="fieldShellClass('email', focusedField === 'email', form.email)">
              <span class="field-floating-label">Email*</span>
              <input
                v-model.trim="form.email"
                type="email"
                class="field-control"
                @focus="focusedField = 'email'"
                @blur="focusedField = ''"
              >
            </div>
            <span v-if="errors.email" class="error-text">{{ errors.email }}</span>
          </label>

          <label class="field-block">
            <div
              class="field-shell phone-shell"
              :class="fieldShellClass('phone', focusedField === 'phone', form.phoneRaw)"
              @focusin="focusedField = 'phone'"
              @focusout="focusedField = ''"
            >
              <span class="field-floating-label">Phone number*</span>
              <input
                ref="phoneInputRef"
                type="tel"
                class="field-control phone-control"
                @input="syncPhone"
                @blur="syncPhone"
              >
            </div>
            <span v-if="errors.phone" class="error-text">{{ errors.phone }}</span>
          </label>

          <label class="field-block">
            <div class="field-shell" :class="fieldShellClass('password', focusedField === 'password', form.password)">
              <span class="field-floating-label">Password*</span>
              <input
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                class="field-control pr-24"
                @focus="focusedField = 'password'"
                @blur="focusedField = ''"
              >
              <div class="field-actions">
                <button type="button" class="action-btn" title="Generate strong password" @click="generateStrongPassword">
                  <i class="ri-magic-line"></i>
                </button>
                <button type="button" class="action-btn" @click="showPassword = !showPassword">
                  <i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
                </button>
              </div>
            </div>
            <div class="mt-3">
              <div class="strength-row">
                <div class="strength-track">
                  <div class="strength-fill" :class="strengthClass" :style="{ width: `${passwordStrength.score}%` }"></div>
                </div>
                <span class="strength-label" :class="strengthClass">{{ passwordStrength.label }}</span>
              </div>
              <p class="strength-copy">Use 8+ characters with uppercase, lowercase, number, and symbol.</p>
            </div>
            <span v-if="errors.password" class="error-text">{{ errors.password }}</span>
          </label>

          <label class="field-block">
            <div class="field-shell" :class="fieldShellClass('confirmPassword', focusedField === 'confirmPassword', form.confirmPassword)">
              <span class="field-floating-label">Confirm password*</span>
              <input
                v-model="form.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                class="field-control pr-12"
                @focus="focusedField = 'confirmPassword'"
                @blur="focusedField = ''"
              >
              <button type="button" class="action-btn single-action" @click="showConfirmPassword = !showConfirmPassword">
                <i :class="showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
              </button>
            </div>
            <span v-if="errors.confirmPassword" class="error-text">{{ errors.confirmPassword }}</span>
          </label>
        </div>

        <div class="mt-10 flex justify-end gap-3">
          <p v-if="submitError" class="mr-auto self-center text-sm font-semibold text-red-600">{{ submitError }}</p>
          <button
            type="submit"
            :disabled="loading"
            class="rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brandDark"
          >
            {{ loading ? 'Signing up...' : 'Sign up' }}
          </button>
        </div>
      </form>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import intlTelInput from 'intl-tel-input'
import 'intl-tel-input/build/css/intlTelInput.css'
import Swal from 'sweetalert2'
import api from '@/services/api'
import { setAuthSession, setAuthToken, setAuthUser } from '@/services/auth'

const router = useRouter()

const form = reactive({
  brandName: '',
  email: '',
  phone: '',
  phoneRaw: '',
  password: '',
  confirmPassword: ''
})

const errors = reactive({})
const phoneInputRef = ref(null)
const phoneIti = ref(null)
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const focusedField = ref('')
const loading = ref(false)
const submitError = ref('')

const fieldShellClass = (key, focused, value) => ({
  'field-shell-active': focused || Boolean(value),
  'field-shell-error': Boolean(errors[key])
})

const validateForm = () => {
  Object.keys(errors).forEach((key) => delete errors[key])
  const phoneDigits = (form.phoneRaw || '').replace(/\D/g, '')
  const utilsLoaded = typeof window !== 'undefined' && typeof window.intlTelInputUtils !== 'undefined'

  if (!form.brandName) errors.brandName = 'Vendor name is required'
  if (!form.email) errors.email = 'Email is required'
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Enter a valid email'
  if (!form.phoneRaw) errors.phone = 'Phone number is required'
  if (phoneDigits && phoneDigits.length > 15) errors.phone = 'Phone number must be 15 digits or less'
  if (phoneDigits && phoneDigits.length < 6) errors.phone = 'Enter a valid phone number'
  if (phoneDigits && utilsLoaded && phoneIti.value && form.phone && !phoneIti.value.isValidNumber()) errors.phone = 'Enter a valid phone number'
  if (!form.password) errors.password = 'Password is required'
  if (form.password && form.password.length < 8) errors.password = 'Password must be at least 8 characters'
  if (!form.confirmPassword) errors.confirmPassword = 'Confirm password is required'
  if (form.password && form.confirmPassword && form.password !== form.confirmPassword) errors.confirmPassword = 'Passwords do not match'

  return Object.keys(errors).length === 0
}

const getPasswordStrength = () => {
  const value = form.password || ''
  let points = 0
  if (value.length >= 8) points += 25
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) points += 25
  if (/\d/.test(value)) points += 25
  if (/[^A-Za-z0-9]/.test(value)) points += 25

  let label = 'Too weak'
  if (points >= 100) label = 'Strong'
  else if (points >= 75) label = 'Good'
  else if (points >= 50) label = 'Fair'
  return { score: points, label }
}

const passwordStrength = computed(() => getPasswordStrength())

const strengthClass = computed(() => {
  if (passwordStrength.value.score >= 100) return 'strength-strong'
  if (passwordStrength.value.score >= 75) return 'strength-good'
  if (passwordStrength.value.score >= 50) return 'strength-fair'
  return 'strength-weak'
})

const generateStrongPassword = () => {
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lowercase = 'abcdefghijkmnopqrstuvwxyz'
  const numbers = '23456789'
  const symbols = '!@#$%^&*_-+='
  const all = uppercase + lowercase + numbers + symbols
  const chars = [
    uppercase[Math.floor(Math.random() * uppercase.length)],
    lowercase[Math.floor(Math.random() * lowercase.length)],
    numbers[Math.floor(Math.random() * numbers.length)],
    symbols[Math.floor(Math.random() * symbols.length)]
  ]
  while (chars.length < 12) {
    chars.push(all[Math.floor(Math.random() * all.length)])
  }
  form.password = chars.sort(() => Math.random() - 0.5).join('')
  form.confirmPassword = form.password
}

const syncPhone = () => {
  if (!phoneInputRef.value) return
  const digits = (phoneInputRef.value.value || '').replace(/\D/g, '').slice(0, 15)
  form.phoneRaw = digits

  if (phoneIti.value && digits) {
    const dialCode = phoneIti.value.getSelectedCountryData?.()?.dialCode || ''
    const normalized = dialCode ? `+${dialCode}${digits}` : digits
    form.phone = normalized
    if (phoneInputRef.value.value.replace(/\D/g, '').length > 15) {
      phoneIti.value.setNumber(normalized)
    }
  } else {
    form.phone = ''
  }
}

const getPhonePayload = () => {
  const dialCode = phoneIti.value?.getSelectedCountryData?.()?.dialCode || ''
  return {
    s_phone_code: dialCode ? `+${dialCode}` : '',
    s_phone: (form.phoneRaw || '').replace(/\D/g, '')
  }
}

const submit = async () => {
  syncPhone()
  if (!validateForm()) return
  submitError.value = ''
  loading.value = true

  const payload = new FormData()
  const phonePayload = getPhonePayload()
  payload.append('s_email', form.email)
  payload.append('s_phone_code', phonePayload.s_phone_code)
  payload.append('s_phone', phonePayload.s_phone)
  payload.append('s_name', form.brandName)
  payload.append('s_password', form.password)

  try {
    const { data } = await api.registerVendor(payload)
    const isError = data?.status === 0 || data?.status === 'error' || data?.success === false
    if (isError) {
      throw new Error(data?.message || 'Vendor registration failed')
    }

    const token = data?.token || data?.access_token || data?.data?.token || data?.data?.access_token
    const responseData = data?.data && typeof data.data === 'object' ? data.data : null
    const user =
      data?.user ||
      data?.data?.user ||
      data?.data?.profile ||
      data?.profile ||
      (responseData
        ? {
            s_id: responseData.s_id,
            s_name: responseData.name || form.brandName,
            s_email: responseData.email || form.email,
            s_phone: responseData.phone || phonePayload.s_phone,
            s_phone_code: responseData.phone_code || phonePayload.s_phone_code
          }
        : null)

    if (token) {
      setAuthToken(token)
    } else {
      setAuthSession()
    }

    if (user) {
      setAuthUser(user)
    } else {
      setAuthUser({
        s_name: form.brandName,
        s_email: form.email,
        s_phone_code: phonePayload.s_phone_code,
        s_phone: phonePayload.s_phone
      })
    }

    await Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: data?.msg || data?.message || 'Registered successfully',
      showConfirmButton: false,
      timer: 1800,
      timerProgressBar: true
    })

    router.push('/dashboard')
  } catch (err) {
    submitError.value = err?.response?.data?.message || err?.message || 'Vendor registration failed'
  } finally {
    loading.value = false
  }
}

const initPhoneInput = () => {
  if (!phoneInputRef.value || phoneIti.value) return
  phoneIti.value = intlTelInput(phoneInputRef.value, {
    initialCountry: 'in',
    separateDialCode: true,
    nationalMode: false,
    preferredCountries: ['in', 'us', 'gb', 'ae'],
    autoPlaceholder: 'polite',
    loadUtils: () => import('intl-tel-input/utils')
  })
  syncPhone()
}

onMounted(() => {
  initPhoneInput()
})

onUnmounted(() => {
  if (phoneIti.value) {
    phoneIti.value.destroy()
    phoneIti.value = null
  }
})
</script>

<style scoped>
.field-block {
  display: block;
}

.field-shell {
  position: relative;
  min-height: 72px;
  border: 1px solid #d8dde5;
  border-radius: 18px;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.field-shell-active {
  border-color: #ec2263;
  box-shadow: 0 0 0 4px rgba(236, 34, 99, 0.08);
}

.field-shell-error {
  border-color: #ef4444;
}

.field-floating-label {
  position: absolute;
  left: 18px;
  top: 24px;
  font-size: 16px;
  color: #9ca3af;
  pointer-events: none;
  transition: transform 0.18s ease, top 0.18s ease, font-size 0.18s ease, color 0.18s ease;
}

.field-shell-active .field-floating-label {
  top: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.field-control,
.field-button {
  width: 100%;
  height: 100%;
  min-height: 72px;
  border: 0;
  border-radius: 18px;
  background: transparent;
  padding: 28px 18px 12px;
  font-size: 16px;
  color: #111827;
}

.field-control:focus,
.field-button:focus,
.dropdown-search-input:focus {
  outline: none;
}

.field-shell-button {
  cursor: pointer;
}

.field-button {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  text-align: left;
}

.field-icon {
  color: #9ca3af;
  font-size: 18px;
  transition: transform 0.2s ease;
}

.dropdown-wrap {
  position: relative;
}

.dropdown-panel {
  margin-top: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.dropdown-search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid #edf0f4;
}

.dropdown-search-input {
  width: 100%;
  border: 0;
  background: transparent;
  font-size: 14px;
  color: #111827;
}

.dropdown-option {
  display: block;
  width: 100%;
  padding: 13px 16px;
  border: 0;
  background: #fff;
  text-align: left;
  font-size: 14px;
  color: #111827;
  transition: background-color 0.15s ease;
}

.dropdown-option:hover {
  background: #f8fafc;
}

.dropdown-empty {
  padding: 16px;
  font-size: 14px;
  color: #94a3b8;
}

.phone-shell {
  overflow: visible;
  min-height: 92px;
}

.phone-control {
  padding-right: 18px;
}

.phone-shell .field-floating-label {
  left: 18px;
  top: 14px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.phone-shell.field-shell-active .field-floating-label {
  top: 14px;
}

.error-text {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #ef4444;
  font-weight: 600;
}

.field-actions {
  position: absolute;
  right: 14px;
  top: 50%;
  display: flex;
  gap: 6px;
  transform: translateY(-50%);
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 999px;
  background: rgba(156, 163, 175, 0.12);
  color: #6b7280;
}

.single-action {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
}

.strength-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.strength-track {
  flex: 1;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #eceff3;
}

.strength-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.2s ease, background-color 0.2s ease;
}

.strength-label {
  min-width: 64px;
  font-size: 12px;
  font-weight: 700;
  text-align: right;
}

.strength-copy {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
}

.strength-weak {
  color: #dc2626;
  background: #dc2626;
}

.strength-fair {
  color: #d97706;
  background: #f59e0b;
}

.strength-good {
  color: #2563eb;
  background: #3b82f6;
}

.strength-strong {
  color: #059669;
  background: #10b981;
}

:deep(.iti) {
  width: 100%;
  height: 92px;
  display: block;
  position: relative;
}

:deep(.iti__flag-container) {
  top: 38px;
  bottom: 10px;
  left: 14px;
  width: 96px;
  display: flex;
  align-items: center;
  border-right: 1px solid #d9dee7;
  background: transparent;
}

:deep(.iti__selected-country) {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  padding-left: 0;
  padding-bottom: 0;
  background: transparent;
}

:deep(.iti__selected-country-primary) {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px 0 8px;
  margin-right: 0;
  position: static;
}

:deep(.iti__selected-country-primary)::after {
  display: none;
}

:deep(.iti__arrow) {
  margin-left: 0;
  border-top-color: #6b7280;
}

:deep(.iti--separate-dial-code .iti__selected-dial-code) {
  color: #374151;
  font-size: 16px;
  font-weight: 600;
  margin-left: 6px;
}

:deep(.iti--separate-dial-code .iti__tel-input) {
  height: 92px;
  padding-top: 40px;
  padding-bottom: 14px;
  padding-left: 122px !important;
  padding-right: 18px;
  border: 0;
  background: transparent;
  font-size: 15px;
  line-height: 1.2;
  color: #111827;
  box-sizing: border-box;
}

:deep(.iti__country-list) {
  z-index: 60;
  top: calc(100% + 8px);
  left: 0;
  right: auto;
  width: 320px !important;
  min-width: 320px;
  max-width: min(320px, calc(100vw - 32px));
  max-height: 300px;
  margin-top: 0;
  border: 1px solid #d8dde5;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.12);
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
}

:deep(.iti__search-container) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
  border-bottom: 1px solid #edf0f4;
}

:deep(.iti__search-container)::before {
  content: '\ea6e';
  position: absolute;
  top: 50%;
  left: 14px;
  transform: translateY(-50%);
  font-family: remixicon;
  font-size: 15px;
  color: #9ca3af;
  pointer-events: none;
}

:deep(.iti__search-input),
:deep(.iti__search-input input),
:deep(.iti input[type='search']) {
  width: 100%;
  height: 46px;
  border: 0;
  border-radius: 18px 18px 0 0;
  padding: 0 14px 0 38px;
  font-size: 14px;
  color: #111827;
  background: #fff;
  box-sizing: border-box;
}

:deep(.iti__search-input:focus),
:deep(.iti__search-input input:focus),
:deep(.iti input[type='search']:focus) {
  outline: none;
}

:deep(.iti__country) {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
}

:deep(.iti__country-name) {
  color: #111827;
  white-space: normal;
}

:deep(.iti__dial-code) {
  margin-left: auto;
  color: #94a3b8;
}

:deep(.iti__flag) {
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.06);
}

:deep(.iti input::placeholder) {
  color: transparent;
}
</style>
